import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { articles } from "@/mocks/articles";

const navItems = [
  { to: "/", label: "首页" },
  { to: "/categories", label: "分类" },
  { to: "/specials", label: "专题" },
  { to: "/search", label: "搜索" },
  { to: "/about", label: "关于" },
];

export default function Header() {
  const location = useLocation();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [focused, setFocused] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  const isHome = location.pathname === "/" || location.pathname === "/specials";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setFocused(false);
  }, [location.pathname]);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setFocused(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const suggestions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return articles
      .filter(
        (item) =>
          item.title.toLowerCase().includes(q) ||
          item.tags.some((tag) => tag.toLowerCase().includes(q)) ||
          item.excerpt.toLowerCase().includes(q)
      )
      .slice(0, 6);
  }, [query]);

  const solid = scrolled || !isHome || mobileOpen;

  const submitSearch = (keyword: string) => {
    const value = keyword.trim();
    if (!value) return;
    setFocused(false);
    navigate(`/search?q=${encodeURIComponent(value)}`);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        solid ? "bg-background-50/95 backdrop-blur border-b border-foreground-200/60" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-16 md:h-20 items-center gap-4 px-4 md:px-8">
        <Link to="/" className="flex items-center gap-2 shrink-0">
          <span
            className={`flex h-9 w-9 items-center justify-center rounded-md font-heading text-lg font-bold ${
              solid ? "bg-foreground-950 text-background-50" : "bg-background-50 text-foreground-950"
            }`}
          >
            黑
          </span>
          <span
            className={`font-heading text-xl md:text-2xl font-bold tracking-wide ${
              solid ? "text-foreground-950" : "text-background-50"
            }`}
          >
            黑丝
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1 ml-6">
          {navItems.map((item) => {
            const active =
              item.to === "/" ? location.pathname === "/" : location.pathname.startsWith(item.to);
            return (
              <Link
                key={item.to}
                to={item.to}
                className={`rounded-md px-3 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
                  solid
                    ? active
                      ? "text-primary-600"
                      : "text-foreground-700 hover:text-foreground-950"
                    : active
                    ? "text-background-50"
                    : "text-background-100/80 hover:text-background-50"
                }`}
              >
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="flex-1" />

        <div ref={boxRef} className="relative hidden md:block w-64 lg:w-72">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              submitSearch(query);
            }}
          >
            <span
              className={`pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base ${
                solid ? "text-foreground-400" : "text-background-100/70"
              }`}
            >
              <i className="ri-search-line" />
            </span>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onFocus={() => setFocused(true)}
              placeholder="搜索搭配灵感 / 潮流资讯"
              className={`h-10 w-full rounded-full border pl-9 pr-4 text-sm outline-none transition-colors ${
                solid
                  ? "border-foreground-200 bg-background-100 text-foreground-950 placeholder:text-foreground-400 focus:border-primary-400"
                  : "border-background-50/40 bg-background-50/15 text-background-50 placeholder:text-background-100/70 focus:border-background-50/70"
              }`}
            />
          </form>
          {focused && query.trim() && (
            <div className="absolute left-0 right-0 top-12 overflow-hidden rounded-lg border border-foreground-200 bg-background-50 py-2 shadow-sm">
              {suggestions.length > 0 ? (
                suggestions.map((item) => (
                  <Link
                    key={item.id}
                    to={`/detail/${item.id}`}
                    onClick={() => setFocused(false)}
                    className="flex items-center gap-3 px-3 py-2 transition-colors hover:bg-background-100"
                  >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-md bg-background-200">
                      <img src={item.cover} alt={item.title} className="h-full w-full object-cover object-top" />
                    </span>
                    <span className="line-clamp-1 text-sm text-foreground-800">{item.title}</span>
                  </Link>
                ))
              ) : (
                <p className="px-3 py-2 text-sm text-foreground-500">没有找到相关建议，按回车试试搜索</p>
              )}
            </div>
          )}
        </div>

        <button
          type="button"
          aria-label="打开菜单"
          onClick={() => setMobileOpen((v) => !v)}
          className={`md:hidden flex h-10 w-10 items-center justify-center rounded-md text-xl ${
            solid ? "text-foreground-950" : "text-background-50"
          }`}
        >
          <i className={mobileOpen ? "ri-close-line" : "ri-menu-line"} />
        </button>
      </div>

      {mobileOpen && (
        <div className="md:hidden border-t border-foreground-200/60 bg-background-50 px-4 pb-5 pt-4">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              submitSearch(query);
            }}
            className="relative mb-4"
          >
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-foreground-400">
              <i className="ri-search-line" />
            </span>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="搜索搭配灵感 / 潮流资讯"
              className="h-11 w-full rounded-full border border-foreground-200 bg-background-100 pl-9 pr-4 text-sm text-foreground-950 outline-none placeholder:text-foreground-400 focus:border-primary-400"
            />
          </form>
          <nav className="flex flex-col">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className="rounded-md px-3 py-3 text-base font-medium text-foreground-800 transition-colors hover:bg-background-100"
              >
                {item.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}